import { RemoteSignButton } from '../RemoteSignButton';
import React, { useState } from 'react';
import { Check, X, Unlink, Plus, Link2, Users, Percent, FileDown, Loader2, FileSignature, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AgencyAvatar, EmptyState } from './shared';
import { InviteDialog, SuperLinkDialog } from './PartnerDialogs';
import { ExternalPartnerDialog } from './ExternalPartnerDialog';
import { ContractSummary, PendingChangeBanner, ContractEditDialog } from './ContractDialogs';
import { ContractPdfButton, SignatureStatus, ContractSignDialog } from './ContractSignature';

const errMsg = (e) => e?.response?.data?.detail || 'Erreur';

const StatementButton = ({ p }) => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/partnerships/${p.id}/commission-statement/pdf`, { params: { year }, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      link.download = `releve_commissions_${(p.partner?.name || 'partenaire').replace(/\s+/g, '_')}_${year}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('Relevé téléchargé');
    } catch (e) { toast.error('Relevé indisponible'); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1 rounded-xl border border-[#2B3A2C]/20 pl-2">
      <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-transparent text-xs font-semibold outline-none" data-testid={`statement-year-${p.id}`}>
        {[0, 1, 2].map((o) => <option key={o} value={thisYear - o}>{thisYear - o}</option>)}
      </select>
      <Button size="sm" variant="ghost" onClick={download} disabled={busy} className="rounded-xl" data-testid={`statement-download-${p.id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />} Relevé PDF
      </Button>
    </div>
  );
};

const PartnerCard = ({ p, isAdmin, onAction, own, onEdit, reload, onSign }) => {
  const pending = p.status === 'pending';
  return (
    <div className="bg-white rounded-2xl border border-[#B85C38]/15 p-5 flex flex-col sm:flex-row sm:items-center gap-4 shadow-sm" data-testid={`partnership-card-${p.id}`}>
      <AgencyAvatar agency={p.partner} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-[#2B3A2C]">{p.partner?.name}</p>
          <Badge className={pending ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
            {pending ? (p.incoming ? 'Invitation reçue' : 'En attente de réponse') : 'Actif'}
          </Badge>
          {p.linked_by_super_admin && <Badge variant="outline" className="text-[10px]">Relié par le super admin</Badge>}
          {p.external && <Badge variant="outline" className="text-[10px] border-[#B85C38]/40 text-[#B85C38]" data-testid={`partnership-external-badge-${p.id}`}>Saisie manuelle · hors plateforme</Badge>}
        </div>
        <p className="text-xs text-gray-500 mt-1">{[p.partner?.contact_name, p.partner?.city, p.partner?.phone, p.partner?.email].filter(Boolean).join(' · ')}</p>
        <p className="text-xs text-[#B85C38] mt-1 flex items-center gap-1">
          <Percent className="w-3 h-3" /> {p.commission_split}% de la commission pour l'agence qui recommande le client
        </p>
        {p.message && <p className="text-xs text-gray-600 italic mt-1">« {p.message} »</p>}
        {!pending && <ContractSummary p={p} />}
        {!pending && <SignatureStatus p={p} own={own} />}
        <PendingChangeBanner p={p} isAdmin={isAdmin} own={own} onDone={reload} />
      </div>
      {!isAdmin && !pending && <ContractPdfButton p={p} />}
      {isAdmin && (
        <div className="flex gap-2 flex-wrap">
          {!pending && <ContractPdfButton p={p} />}
          {!pending && !p.pending_change && !p.signatures?.[own] && (
            <Button size="sm" onClick={() => onSign({ p, forPartner: false })} className="rounded-xl bg-[#2B3A2C] hover:bg-[#1f2b20] text-white" data-testid={`contract-sign-${p.id}`}>
              <PenLine className="w-4 h-4 mr-1" /> Signer
            </Button>
          )}
          {!pending && !p.pending_change && p.external && !p.signatures?.[p.partner?.id] && (
            <Button size="sm" variant="outline" onClick={() => onSign({ p, forPartner: true })} className="rounded-xl border-[#2B3A2C]/25" data-testid={`contract-sign-partner-${p.id}`}>
              <PenLine className="w-4 h-4 mr-1" /> Faire signer le partenaire
            </Button>
          )}
          {!pending && !p.pending_change && p.external && <RemoteSignButton docType="partnership" docId={p.id} />}
          {!pending && <StatementButton p={p} />}
          {!pending && !p.pending_change && (
            <Button size="sm" variant="outline" onClick={() => onEdit(p)} className="rounded-xl border-[#2B3A2C]/25" data-testid={`partnership-edit-${p.id}`}>
              <FileSignature className="w-4 h-4 mr-1" /> Modifier le contrat
            </Button>
          )}
          {pending && p.incoming && (
            <>
              <Button size="sm" onClick={() => onAction(p, 'accept')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid={`partnership-accept-${p.id}`}>
                <Check className="w-4 h-4 mr-1" /> Accepter
              </Button>
              <Button size="sm" variant="outline" onClick={() => onAction(p, 'reject')} className="rounded-xl" data-testid={`partnership-reject-${p.id}`}>
                <X className="w-4 h-4 mr-1" /> Refuser
              </Button>
            </>
          )}
          {(!pending || !p.incoming) && (
            <Button size="sm" variant="outline" onClick={() => onAction(p, 'end')} className="rounded-xl text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`partnership-end-${p.id}`}>
              <Unlink className="w-4 h-4 mr-1" /> {pending ? 'Annuler' : 'Mettre fin'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export const PartnersTab = ({ isAdmin, isSuper, partnerships, directory, reload }) => {
  const [invite, setInvite] = useState(false);
  const [link, setLink] = useState(false);
  const [external, setExternal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [signing, setSigning] = useState(null);
  const { user } = useAuth();

  const onAction = async (p, action) => {
    if (action === 'end' && !window.confirm(`Mettre fin au partenariat avec ${p.partner?.name} ?`)) return;
    try {
      await api.post(`/partnerships/${p.id}/${action}`);
      toast.success({ accept: 'Partenariat accepté', reject: 'Invitation refusée', end: 'Partenariat terminé' }[action]);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setInvite(true)} className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="partnership-invite-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Inviter une agence
          </Button>
          <Button variant="outline" onClick={() => setExternal(true)} className="rounded-xl border-[#B85C38]/40 text-[#B85C38]" data-testid="partnership-external-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Saisir une agence manuellement
          </Button>
          {isSuper && (
            <Button variant="outline" onClick={() => setLink(true)} className="rounded-xl border-[#2B3A2C]/30" data-testid="partnership-super-link-btn">
              <Link2 className="w-4 h-4 mr-1.5" /> Relier deux agences
            </Button>
          )}
        </div>
      )}
      {partnerships.length === 0
        ? <EmptyState icon={Users} text="Aucun partenariat pour le moment. Invitez une agence depuis l'annuaire." testid="partners-empty" />
        : partnerships.map((p) => <PartnerCard key={p.id} p={p} isAdmin={isAdmin} onAction={onAction} own={user?.agency_id} onEdit={setEditing} reload={reload} onSign={setSigning} />)}
      <ContractSignDialog target={signing} onClose={() => setSigning(null)} onDone={reload} />
      <ContractEditDialog p={editing} onClose={() => setEditing(null)} onDone={reload} />
      <InviteDialog open={invite} onClose={() => setInvite(false)} directory={directory} onDone={reload} />
      <ExternalPartnerDialog open={external} onClose={() => setExternal(false)} onDone={reload} />
      {isSuper && <SuperLinkDialog open={link} onClose={() => setLink(false)} onDone={reload} />}
    </div>
  );
};
